import type { ReactNode } from 'react';
import type { SafetyApprovalAttachment, WorkOrderForm344 } from '@railroute/contracts/core';
import { FORM_HEADINGS, measureRows, sheetGaps, sheetPositions, type SheetSignature } from './positions';
import { briefingLines, briefingLineText } from './briefing';
import './sheet.css';

// Лист наряда-допуска по приложению 1 к Правилам № 344.
//
// Инспектор при разборе смотрит наряд ЦЕЛИКОМ и ищет в нём пункты 6, 7, 8, 9,
// 10. Поэтому лист идёт по позициям бланка, а подписи стоят под теми
// позициями, которые они удостоверяют, — а не общим списком в конце.
//
// Одна разметка на экран и на бумагу: печатается ровно то, что человек видит.

const LEVELS: Record<string, string> = {
  QUALIFIED_ECP: 'квалифицированная ЭЦП',
  INTERNAL_DEVICE: 'ключ устройства',
};
const VERIFICATION: Record<string, string> = {
  VERIFIED: 'подпись подтвердил провайдер',
  DEVICE_KEY_VERIFIED: 'проверена ключом устройства',
  UNVERIFIED: 'проверка не выполнялась',
  FAILED: 'проверка не прошла',
};
const UNLOCK: Record<string, string> = {
  BIOMETRIC: 'подтверждено биометрией',
  DEVICE_CREDENTIAL: 'подтверждено кодом устройства',
};
// Класс напряжения (issue #463) — словами, как и всё на листе: `ABOVE_1000V`
// читается как машинный код, а не как сведение о том, на какой установке
// велась работа.
const VOLTAGE_CLASSES: Record<string, string> = {
  ABOVE_1000V: 'выше 1000 В',
  UP_TO_1000V: 'до 1000 В',
};
// Состояние наряда — НЕ поле бланка, а наше служебное. Поэтому оно, во-первых,
// по-русски: `ISSUED` на листе читается как опечатка машины, а не как
// сведение; во-вторых, вынесено из бланковой части вниз отдельным блоком.
const STATUS: Record<string, string> = {
  ISSUED: 'выписан',
  ADMITTED: 'допуск оформлен',
  IN_PROGRESS: 'работа идёт',
  CLOSED: 'закрыт',
};

const moment = (iso: string | null): string => {
  if (!iso) return '—';
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString('ru-RU');
};

const thumb = (value: string | null) =>
  value ? (value.slice(0, 16).match(/.{1,4}/g) ?? []).join(' ').toUpperCase() : null;

function Signature({ signature }: { signature: SheetSignature }) {
  const meta = [
    moment(signature.signedAt),
    LEVELS[signature.level] ?? signature.level,
    VERIFICATION[signature.verification] ?? signature.verification,
    signature.unlock ? UNLOCK[signature.unlock] : null,
    thumb(signature.deviceThumbprint) ? `телефон ${thumb(signature.deviceThumbprint)}` : null,
  ]
    .filter(Boolean)
    .join(' · ');
  return (
    <div className="signature">
      {/* Issue #471: подписант графы 8.1 — представитель чужой организации, у
          которого табельного номера НЕ БЫВАЕТ. Пустые скобки после фамилии
          читались бы как «номер потерян», поэтому у него в скобках стоит то,
          чем он на самом деле удостоверен: должность и организация. */}
      <div>
        {signature.itemNumber ? `${signature.itemNumber} — ` : ''}
        {signature.name} (
        {signature.externalSigner
          ? `${signature.externalSigner.position}, ${signature.externalSigner.organizationName}`
          : signature.employeeNumber}
        )
      </div>
      <div className="signature__meta">{meta}</div>
    </div>
  );
}

// Issue #469: значение бывает не только строкой — у приложения к согласованию
// рядом с реквизитами стоит кнопка «Открыть». Правило прочерка от этого не
// меняется: пусто — «—», как велит пункт 5 Правил № 344.
function Field({ label, value }: { label: string; value: ReactNode }) {
  const empty = value === null || value === undefined || value === '';
  return (
    <div className="field">
      <div className="field__label">{label}</div>
      <div className="field__value">{empty ? '—' : value}</div>
    </div>
  );
}

const personLine = (person: { name: string; employeeNumber: string } | null) =>
  person ? `${person.name} (${person.employeeNumber})` : null;

export function Form344Sheet({
  form,
  onOpenAttachment,
}: {
  form: WorkOrderForm344;
  /**
   * Открыть приложенный к согласованию документ (issue #469).
   *
   * Необязателен: лист — чистая разметка, и байты приложения он не читает.
   * Обращение к серверу делает раздел, у которого есть токен. Обработчика нет
   * (печать, тесты разметки) — приложения перечисляются без кнопки: реквизиты
   * и контрольная сумма на бумаге нужны и сами по себе.
   */
  onOpenAttachment?: (attachment: SafetyApprovalAttachment) => void;
}) {
  const positions = sheetPositions(form);
  const measures = measureRows(form);
  const gaps = sheetGaps(form);
  const position = (key: string) => positions.find((p) => p.key === key)!;

  // Подписи позиции, а при их отсутствии — одна строка словами. Пустое место
  // читается как недорисованный экран, поэтому позиция без подписи говорит об
  // этом сама. Графа, которая этому наряду не требуется, печатается ПРОЧЕРКОМ
  // (пункт 5 Правил № 344): «подписи нет» сказало бы о ней, что подпись
  // ожидалась, — а её никто не ждал.
  const signatures = (key: string) => {
    const { signatures: rows, dash } = position(key);
    if (rows.length === 0) {
      return (
        <div className="signature signature--none">
          {dash ? (
            <>
              <span className="measure__dash">—</span> {dash}
            </>
          ) : (
            'подписи нет'
          )}
        </div>
      );
    }
    return rows.map((signature) => <Signature key={signature.id} signature={signature} />);
  };

  return (
    <div className="sheet">
      <h1 className="sheet__title">НАРЯД-ДОПУСК № {form.workOrder.number}</h1>
      <p className="sheet__subtitle">
        на выполнение работ повышенной опасности · приложение 1 к Правилам № 344
      </p>

      {/* Чего под нарядом нет — до содержания, а не в конце: документ не
          должен выглядеть полным, если он не полон. */}
      {gaps.length > 0 && (
        <div className="gaps">
          <p className="gaps__title">Под нарядом не хватает:</p>
          <ul>
            {gaps.map((gap) => (
              <li key={gap.key}>{gap.text}</li>
            ))}
          </ul>
        </div>
      )}

      <section className="block">
        <h2 className="block__title">Организация</h2>
        <Field label="Организация" value={form.organization.name} />
        <Field label="Подразделение" value={form.subdivisionName} />
        <Field label="Выдан" value={moment(form.workOrder.issuedAt)} />
      </section>

      {/* Пункты 1 и 3 стоят в ШАПКЕ бланка, до мероприятий: бланк называет
          производителя работ и допускающего до того, как описывает, что и как
          готовится. Их подписи — ниже, в пунктах 10 и 9. */}
      <section className="block">
        <h2 className="block__title">{FORM_HEADINGS.producer}</h2>
        <Field label="Производитель работ" value={personLine(form.roles.producer)} />
        <div className="signature signature--none">подпись этой позиции — в пункте 10</div>
      </section>

      <section className="block">
        <h2 className="block__title">{FORM_HEADINGS.scope}</h2>
        <Field label="Место работ" value={form.workOrder.workSiteName} />
        <Field label="Уточнение места" value={form.workOrder.workSiteDetail} />
        <Field
          label="Объём работ"
          value={form.workOrder.workTypeCatalogName ?? form.workOrder.workType}
        />
      </section>

      <section className="block">
        <h2 className="block__title">{FORM_HEADINGS.admitting}</h2>
        <Field label="Допускающий" value={personLine(form.roles.admitting)} />
        <div className="signature signature--none">подпись этой позиции — в пункте 9</div>
      </section>

      <section className="block">
        <h2 className="block__title">{FORM_HEADINGS.responsible}</h2>
        <Field label="Назначен" value={personLine(form.roles.responsible)} />
        <div className="signature signature--none">
          подписной строки у этой позиции в бланке нет — руководитель назначается, но наряд не подписывает
        </div>
      </section>

      <section className="block">
        <h2 className="block__title">{FORM_HEADINGS.measures}</h2>
        {measures.map((row) => (
          <div className="measure" key={row.itemNumber}>
            <div className="measure__no">{row.itemNumber}</div>
            <div className="measure__body">
              <div>
                <strong>{row.title}</strong>
                {row.hint ? <span className="measure__hint"> — {row.hint}</span> : null}
              </div>
              {/* Прочерк, а не пропуск: его отсутствие читается как потерянная строка. */}
              <div>{row.content ?? <span className="measure__dash">—</span>}</div>
              {row.executor ? (
                <div className="measure__hint">Исполнитель: {personLine(row.executor)}</div>
              ) : null}
              {row.signature ? <Signature signature={row.signature} /> : null}
            </div>
          </div>
        ))}
      </section>

      {/* Полей с такими названиями в приложении 1 НЕТ. Они наши: группа по
          электробезопасности — отраслевое требование к железнодорожному
          контуру, «меры безопасности (общие)» — поле прежней формы наряда, на
          байтах которого стоят уже выпущенные подписи (ADR-004 §5), поэтому
          убрать его нельзя.

          Стоять они обязаны ОТДЕЛЬНО и с прямой оговоркой: поле, показанное
          среди пунктов бланка, читается как пункт бланка, а при разборе это
          означает ссылку на норму, которой нет. */}
      <section className="block block--addendum">
        <h2 className="block__title">Дополнения к бланку</h2>
        <p className="block__note">
          Приложением 1 к Правилам № 344 эти поля не предусмотрены — они ведутся сверх бланка.
        </p>
        <Field label="Группа по электробезопасности" value={form.workOrder.requiredElectricalGroup} />
        {/* Issue #463 (B-075): напряжение стоит сразу под группой — по пунктам
            33, 35, 37 и 38 Правил № 222 порог группы выводится из него, и
            группа без напряжения читается как следствие без основания.
            Прочерк ставит сам `Field`: у наряда, выписанного до issue #419,
            напряжение не названо, и это не «до 1000 В». */}
        <Field label="Напряжение электроустановки" value={VOLTAGE_CLASSES[form.workOrder.voltageClass ?? '']} />
        <Field label="Меры безопасности (общие)" value={form.workOrder.safetyMeasures} />
      </section>

      <section className="block">
        <h2 className="block__title">{position('issue').title}</h2>
        <Field label="Выдающий наряд" value={personLine(form.roles.issuer)} />
        {signatures('issue')}
      </section>

      <section className="block">
        <h2 className="block__title">{position('measures').title}</h2>
        {signatures('measures')}
      </section>

      {/* Графа 8 — ВНУТРЕННЕЕ согласование: его даёт начальник смены
          (участка), где идут работы, то есть работник самой организации со
          своей учётной записью (issue #445 — документ WORK_ORDER_APPROVAL).

          Это позиция БЛАНКА, а не дополнение к нему (issue #467, решение
          владельца 25.09.2026 по сверке с подлинным бланком). До #467 она
          стояла блоком «сверх бланка» с оговоркой, будто приложение 1 такой
          графы не знает, — лист утверждал о норме то, чего в ней нет, ровно
          наоборот к тому, ради чего оговорка и заведена. */}
      <section className="block">
        <h2 className="block__title">{position('internalApproval').title}</h2>
        <Field
          label="Требуется согласование"
          value={form.operationalApproval.required ? 'да' : 'нет'}
        />
        <Field label="Согласовал" value={personLine(form.operationalApproval.approvedBy)} />
        <Field
          label="Дата и время согласования"
          value={moment(form.operationalApproval.approvedAt)}
        />
        {signatures('internalApproval')}
      </section>

      {/* Графа 8.1 — открытая строка бланка без заданной должности: в неё
          ложится представитель ВНЕШНЕЙ организации, обслуживающей
          коммуникации, при работах по пункту 8 Правил № 344 (дороги, ЛЭП,
          газо- и теплопроводы, все земляные работы). Подписывает он ПО ССЫЛКЕ,
          без учётной записи (issue #471): подпись стоит под тем же документом
          выдачи, что и подпись выдающего, и различаются они ролью. Пока графа
          не подписана, лист говорит об этом пробелом, а не пустым местом. */}
      <section className="block">
        <h2 className="block__title">{position('externalApproval').title}</h2>
        <Field label="Требуется согласование" value={form.approval.required ? 'да' : 'нет'} />
        {/* Issue #468 (B-034): с кем именно согласованы работы. До этой задачи
            графа несла только признак «требуется» — то есть утверждала
            обязанность и молчала о её исполнении. Реквизитов бывает несколько:
            работа у автодороги и у кабеля связи разом — две организации.

            Согласований не записано — прочерк, а не пустое место (пункт 5
            Правил № 344): его рисует `Field` сам. */}
        {form.approval.records.length === 0 ? (
          <Field label="Согласовано с" value={null} />
        ) : (
          form.approval.records.map((record) => (
            <div key={record.organizationName}>
              <Field
                label={`Согласовано с: ${record.organizationName}`}
                value={
                  `${record.representativePosition} ${record.representativeName}` +
                  ` · дата согласования ${record.approvedAt}` +
                  (record.permitNumber && record.permitIssuedAt
                    ? ` · письменное разрешение № ${record.permitNumber} от ${record.permitIssuedAt}`
                    : '')
                }
              />
              {/* Issue #469: приложенные документы — письменное разрешение
                  владельца коммуникаций и схема (пункт 300 Правил № 222).
                  Контрольная сумма печатается ВСЕГДА, в том числе на бумаге:
                  по ней предъявленный файл сверяется с подписанным документом
                  выдачи, и лист без неё говорил бы, что документ был, не
                  позволяя проверить, какой именно. */}
              {record.attachments.map((attachment) => (
                <Field
                  key={attachment.id}
                  label="Приложено"
                  value={
                    <>
                      {attachment.fileName} ·{' '}
                      {attachment.uploadedBy.name ?? 'приложивший не назван'} ·{' '}
                      {attachment.uploadedAt.slice(0, 10)} · {attachment.checksum}
                      {onOpenAttachment ? (
                        <>
                          {' '}
                          <button
                            type="button"
                            className="ui-btn ui-btn--ghost screen-only"
                            onClick={() => onOpenAttachment(attachment)}
                          >
                            Открыть
                          </button>
                        </>
                      ) : null}
                    </>
                  }
                />
              ))}
            </div>
          ))
        )}
        {form.approval.waivedBy ? (
          <Field
            label="Снято"
            value={`${personLine(form.approval.waivedBy)} · ${moment(form.approval.waivedAt)}`}
          />
        ) : null}
        {/* Issue #469: основание, по которому наряд потребовал приложенного
            разрешения. Прочерк, когда работ в охранной зоне нет: «нет» — это
            тоже сведение о наряде (пункт 5 Правил № 344). */}
        <Field
          label="Земляные работы в охранной зоне подземных коммуникаций (пункт 300 Правил № 222)"
          value={form.approval.excavationInProtectedZone ? 'да' : null}
        />
        {form.approval.excavationWaivedBy ? (
          <Field
            label="Признак охранной зоны снят"
            value={`${personLine(form.approval.excavationWaivedBy)} · ${moment(form.approval.excavationWaivedAt)}`}
          />
        ) : null}
        {signatures('externalApproval')}
      </section>

      <section className="block">
        <h2 className="block__title">{position('admission').title}</h2>
        <Field label="Допускающий" value={personLine(form.roles.admitting)} />
        <Field label="Дата и время допуска" value={moment(form.workOrder.admittedAt)} />
        {signatures('admission')}
      </section>

      <section className="block">
        <h2 className="block__title">{position('start').title}</h2>
        <Field label="Производитель работ" value={personLine(form.roles.producer)} />
        <Field label="Дата и время" value={moment(form.workOrder.startedAt)} />
        {signatures('start')}
      </section>

      <section className="block">
        <h2 className="block__title">{position('briefing').title}</h2>
        <Field label="Численность бригады" value={`${form.crew.length} чел.`} />
        {form.crew.length === 0 ? (
          <div className="signature signature--none">состав не указан</div>
        ) : (
          form.crew.map((member) => (
            <div className="field" key={member.workerId}>
              <div className="field__label">
                {member.role === 'OBSERVER' ? 'Наблюдающий' : 'Член бригады'}
              </div>
              <div className="field__value">
                {member.name} ({member.employeeNumber})
                {member.professionName ? ` · ${member.professionName}` : ''}
                {member.qualificationGrade ? `, разряд ${member.qualificationGrade}` : ''}
                {briefingLines(member, form.documents).map((line, index) => (
                  <div className="measure__hint" key={index}>
                    {briefingLineText(line, moment)}
                  </div>
                ))}
              </div>
            </div>
          ))
        )}
        <div className="block__note">Целевой инструктаж получили:</div>
        {signatures('briefing')}
      </section>

      <section className="block">
        <h2 className="block__title">{position('shifts').title}</h2>
        {form.shifts.length === 0 ? (
          <div className="signature signature--none">продлений нет</div>
        ) : (
          form.shifts.map((shift) => (
            <div className="field" key={shift.id}>
              <div className="field__label">Смена {shift.shiftNumber}</div>
              <div className="field__value">
                Смену сдал: {shift.handedOverBy.name ?? '—'} · Смену принял:{' '}
                {shift.incomingProducer.name ?? '—'} · Допуск разрешил: {shift.admitting.name ?? '—'}
                <div className="measure__hint">
                  Дата и время: {moment(shift.extendedAt)} · работа начата: {moment(shift.startedAt ?? null)} ·
                  численность заступающей бригады: {shift.incomingCrewSize}
                  {shift.conditionsUnchanged ? ' · условия не изменились' : ' · условия изменились'}
                </div>
              </div>
            </div>
          ))
        )}
        {signatures('shifts')}
      </section>

      <section className="block">
        <h2 className="block__title">{position('closure').title}</h2>
        {/* Пункт 13 — последний в бланке, пункта 14 в нём нет. Сдача наряда
            производителем работ и приёмка рабочего места допускающим — две
            графы ОДНОГО пункта, а не два пункта. */}
        <Field label="Наряд сдал (производитель работ)" value={moment(form.workOrder.closureHandoverAt)} />
        <Field
          label="Рабочее место и наряд принял (допускающий)"
          value={moment(form.workOrder.closedAt)}
        />
        {signatures('closure')}
      </section>

      {/* Служебное — ВНЕ бланковой части и после неё: состояние наряда ведём
          мы, приложение 1 такого поля не знает. */}
      <section className="block block--addendum">
        <h2 className="block__title">Служебные сведения</h2>
        <p className="block__note">Полем бланка не являются — состояние ведёт система.</p>
        <Field
          label="Состояние наряда"
          value={STATUS[form.workOrder.status] ?? form.workOrder.status}
        />
      </section>
    </div>
  );
}
